import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { environment } from '@environments/environment';
import {
  INVOICES_ROUTES,
  MATERIALS_ROUTES,
  PROJECTS_ROUTES,
  QUOTATION_CHAPTERS_ROUTES,
  QUOTATION_MATERIALS_ROUTES,
  QUOTATION_SERVICES_ROUTES,
  QUOTATION_VERSIONS_ROUTES,
  QUOTATIONS_ROUTES,
  UNITS_TYPES_ROUTES,
  STEPS_ROUTES,
  FILES_ROUTES,
  PROJECTS_FILES_ROUTES,
  PLANNINGS_ROUTES,
  STEP_CATEGORIES_ROUTES,
  TRANSACTIONS_ROUTES,
  PROJECT_MATERIALS_ROUTES,
  PROJECT_PAYMENTS_ROUTE,
  PROJECT_SUBJECTS_ROUTE,
  ACCOUNT_ROUTES,
  NOTIFICATIONS_ROUTES,
  PROJECTS_ROOMS_ROUTE,
  QUOTATION_MATERIAL_VALIDATIONS_ROUTE
} from '@providers/routes-config';

@Injectable({
  providedIn: 'root'
})
export class RequestV2Service {
  private url: string;

  constructor(
    private http: HttpClient
  ) {
    this.url = environment.api_url;
  }

  public getAccount(): Promise<any> {
    const END_POINT = ACCOUNT_ROUTES.GET.account;

    return this.http.get(this.url + END_POINT).toPromise();
  }

  public getProjects(userId: any, full: boolean = false, includeTest:boolean= false): Promise<any> {
    const END_POINT = PROJECTS_ROUTES.GET.projects;
    let params = new HttpParams();

    if (userId) {
      params = params.append('uid', userId.toString());
    }

    if (full) {
      params = params.append('full', '1');
    }
    if (includeTest) {
      params = params.append('include_test', 'true');
    }

    return this.http.get(this.url + END_POINT, { params })
      .toPromise();
  }

  // Get specific project
  public getProject(projectId: any, full?: boolean): Promise<any> {
    if (typeof projectId !== 'undefined') {
      const END_POINT = PROJECTS_ROUTES.GET.project.replace(':id', projectId);
      let params = new HttpParams();

      if (full) {
        params = params.append('full', 'true');
      }

      return this.http.get(this.url + END_POINT, { params }).toPromise();
    }

    throw new Error('Couldn\'t get project: id undefined');
  }

  // Get quotations from a project
  public getQuotations(idProject: any): Promise<any> {
    if (typeof idProject !== 'undefined') {
      let params = new HttpParams();

      params = params.append('pid', idProject);

      const END_POINT = QUOTATIONS_ROUTES.GET.quotations;

      return this.http.get(this.url + END_POINT, { params }).toPromise();
    }

    throw new Error('Couldn\'t get specific quotation of project');
  }

  // Get specific quotation of project
  public getQuotation(idQuotation: any): Promise<any> {
    if (typeof idQuotation !== 'undefined') {
      const END_POINT = QUOTATIONS_ROUTES.GET.quotation.replace(':qid', idQuotation);

      return this.http.get(this.url + END_POINT).toPromise();
    }

    throw new Error('Couldn\'t get specific quotation of project');
  }

  // Get versions
  public getVersions(
    idQuotation: any,
    { limit, sort, full }: {limit?: string, sort?: { key: string , value: string}, full?: boolean}
  ): Promise<any> {
    if (typeof idQuotation !== 'undefined') {
      let params = new HttpParams();

      params = params.append('qids', idQuotation.toString());

      if (limit) {
        params = params.append('limit', limit.toString());
      }

      if (sort) {
        params = params.append(`sort[${sort.key}]`, sort.value.toString());
      }

      if (full) {
        params = params.append('full', '1');
      }

      const END_POINT = QUOTATION_VERSIONS_ROUTES.GET.quotation_versions;

      return this.http.get(this.url + END_POINT, { params }).toPromise();
    }

    throw new Error('Id quotation or id version is undefined');
  }

  public getQuotationVersionFinancial(idVersion): Promise<any> {
    if (typeof idVersion !== 'undefined') {
      const END_POINT = QUOTATION_VERSIONS_ROUTES.GET.quotation_financial
        .replace(':vid', idVersion);

      return this.http.get(this.url + END_POINT).toPromise();
    }

    throw new Error('Id version is missing');
  }

  // Get version
  public getVersion(idVersion: string): Promise<any> {
    if (idVersion) {
      const END_POINT = QUOTATION_VERSIONS_ROUTES.GET.quotation_version
        .replace(':vid', idVersion);

      return this.http.get(this.url + END_POINT).toPromise();
    }

    throw new Error('Id quotation or id version is undefined');
  }

  // Get chapters
  public getChapters(idVersion: string, limit?: any): Promise<any> {
    if (idVersion) {
      const END_POINT = QUOTATION_CHAPTERS_ROUTES.GET.quotation_chapters;
      let params = new HttpParams();

      params = params.append('vid', idVersion.toString());

      if (limit) {
        params = params.append('limit', limit.toString());
      }

      return this.http.get(this.url + END_POINT, { params }).toPromise();
    }
  }

  // Get services
  public getServices(idChapter: string, limit?: any): Promise<any> {
    if (idChapter) {
      const END_POINT = QUOTATION_SERVICES_ROUTES.GET.quotation_services;
      let params = new HttpParams();

      params = params.append('cid', idChapter.toString());

      if (limit) {
        params = params.append('limit', limit.toString());
      }

      return this.http.get(this.url + END_POINT, { params }).toPromise();
    }
  }

  // Get quotation materials
  public getQuotationMaterials(
    idService: string,
    idProject:string,
    full:boolean,
    limit:string,
    showOption: string
  ): Promise<any> {
    const END_POINT = QUOTATION_MATERIALS_ROUTES.GET.quotation_materials;
    let params = new HttpParams();

    if (idService) {
      params = params.append('sid', idService.toString());
    }

    if (idProject) {
      params = params.append('pid', idProject.toString());
    }

    if (full) {
      params = params.append('full', 'true');
    }

    if (limit) {
      params = params.append('limit', 'all');
    }

    if (showOption) {
      params = params.append('show_option', showOption);
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  public getMaterials(
    idProject?: any,
    page?:any,
    productCategory?: any,
    idsVersion?:string
  ): Promise<any> {
    let params = new HttpParams();

    if (idProject) {
      params = params.append('pid', idProject);
    }

    if (page) {
      params = params.append('page', page);
    }

    if (productCategory) {
      params = params.append('ids_material_product_categories', productCategory.toString());
    }

    if (idsVersion) {
      params = params.append('ids_material_versions', idsVersion);
    }

    const END_POINT = MATERIALS_ROUTES.GET.materials;

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  // Get Top Materials filtered by categorie
  public getTopAlternativeMaterial(idCategory, allLevel:boolean = false): Promise <any> {
    if (!idCategory) {
      throw new Error('Id category is undefined');
    }

    const END_POINT = MATERIALS_ROUTES.GET.top;
    let params = new HttpParams();

    params = params.append('category', idCategory);

    if (allLevel) {
      params = params.append('use_main_category', 'true');
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  public getUnitTypes(): Promise<any> {
    const END_POINT = UNITS_TYPES_ROUTES.GET.unit_types;
    let params = new HttpParams();

    params = params.append('limit', 'all');

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  /**
   * Get invoices
   */
  public getInvoices(idProject?: number, full: boolean = false): Promise<any> {
    const END_POINT = INVOICES_ROUTES.GET.invoices;
    let params = new HttpParams();

    if (idProject) {
      params = params.append('pid', idProject.toString());
    }

    if (full) {
      params = params.append('full', '1');
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  /**
   * Download quotation version
   * @param idVersion
   * @param mode
   * @param format
   * @param simplified
   */
  public downloadQuotationVersion(
    idVersion: any,
    mode: string,
    format: string = 'pdf',
    simplified: boolean = false
  ): Promise<any> {
    if (idVersion) {
      const END_POINT = QUOTATION_VERSIONS_ROUTES.GET.download_version
        .replace(':vid', idVersion);
      let params = new HttpParams();

      params = params.append('version', mode);
      params = params.append('format', format);
      params = params.append('simplified', simplified.toString());

      return this.http
        .get(this.url + END_POINT, { params , responseType: 'blob', observe: 'response' })
        .toPromise();
    }
  }

  public getProjectSteps(limit?): Promise<any> {
    const END_POINT = STEPS_ROUTES.GET.steps;
    let params = new HttpParams();

    if (limit) {
      params = params.append('limit', 'all');
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  // Download file
  public downloadFile(idFile): Promise<any> {
    const END_POINT = FILES_ROUTES.GET.file
      .replace(':fid', idFile);

    return this.http.get(
      this.url + END_POINT, { responseType: 'blob', observe: 'response' }
    ).toPromise();
  }

  // get project files
  public getProjectFiles(idProject?, full: boolean = false): Promise<any> {
    const END_POINT = PROJECTS_FILES_ROUTES.GET.project_files;
    let params = new HttpParams();

    if (idProject) {
      params = params.append('pid', idProject.toString());
    }

    if (full) {
      params = params.append('full', '1');
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  /**
   * PLANNINGS
   */
  // Get a list of plannings. Either related to severals Projets
  // or to have only templates PLannings
  public getPlannings(idProject?, template?, full?): Promise<any> {
    const END_POINT = PLANNINGS_ROUTES.GET.plannings;
    let params = new HttpParams();

    if (idProject) {
      params = params.append('pid', idProject);
    }

    if (template) {
      params = params.append('template', 'true');
    }

    if (full) {
      params = params.append('full', 'true');
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  // Project step categories
  public getStepCategories(): Promise<any> {
    const END_POINT = STEP_CATEGORIES_ROUTES.GET.step_categories;

    return this.http.get(this.url + END_POINT).toPromise();
  }

  /**
   * Get transactions
   */
  public getTransactions(idProject?: number, full?: boolean): Promise<any> {
    const END_POINT = TRANSACTIONS_ROUTES.GET.transactions;
    let params = new HttpParams();

    if (idProject) {
      params = params.append('pid', idProject.toString());
    }

    if (full) {
      params = params.append('full', '1');
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  /**
   * Get payment requests
   */
  public getProjectMaterials(idProject?: number, full?: boolean, limit?:any): Promise<any> {
    const END_POINT = PROJECT_MATERIALS_ROUTES.GET.project_materials;
    let params = new HttpParams();

    if (idProject) {
      params = params.append('pid', idProject.toString());
    }

    if (full) {
      params = params.append('full', '1');
    }

    if (limit) {
      params = params.append('limit', limit);
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  /**
   * Get project payments
   */
  public getProjectPayments(idProject?: number): Promise<any> {
    const END_POINT = PROJECT_PAYMENTS_ROUTE.GET.project_payments;
    let params = new HttpParams();

    if (idProject) {
      params = params.append('pid', idProject.toString());
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  // Get List of projects with related users
  public getProjectSubjects(idProject, full?): Promise <any> {
    const END_POINT = PROJECT_SUBJECTS_ROUTE.GET.listsubjects;
    let params = new HttpParams();

    if (idProject) {
      params = params.append('pid', idProject.toString());
    }

    if (full) {
      params = params.append('full', full);
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  // Create a subject
  public addProjectSubject(subject): Promise <any> {
    const END_POINT = PROJECT_SUBJECTS_ROUTE.POST.subject;

    return this.http.post(this.url + END_POINT, subject).toPromise();
  }

  // Send notification
  public getNotifications(projectId: any): Promise<any> {
    const END_POINT = NOTIFICATIONS_ROUTES.GET.notifications;
    let params = new HttpParams();

    params = params.append('pid', projectId);

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  // Project Rooms
  public getProjectRooms(project): Promise<any> {
    const END_POINT = PROJECTS_ROOMS_ROUTE.GET.project_rooms;

    if (!project) {
      return null;
    }

    let params = new HttpParams();
    params = params.append('pid', project.id.toString());

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }

  public getListQuotationMaterialValidation(idProject:number, limit:string) {
    const END_POINT = QUOTATION_MATERIAL_VALIDATIONS_ROUTE.GET.quotation_material_validation;
    let params = new HttpParams();

    if (idProject) {
      params = params.append('pid', idProject.toString());
    }

    if (limit) {
      params = params.append('limit', 'all');
    }

    return this.http.get(this.url + END_POINT, { params }).toPromise();
  }
}
